import { useEffect, useRef, useState } from 'react'
import { AdminActionSummary } from './AdminActionSummary'
import { Dialog } from '@/components/ui/Dialog'
import { Button } from '@/components/ui/Button'
import { morphToast } from '@/components/ui/Toast'

interface Props {
  open: boolean
  title: string
  confirmLabel?: string
  preview: () => Promise<Record<string, unknown>>
  apply: (request: { reason: string; idempotency_key: string; fingerprint: string }) => Promise<Record<string, unknown>>
  onClose: () => void
  onApplied: () => void
}

export function SafeActionDialog({ open, title, confirmLabel = 'Aplicar', preview, apply, onClose, onApplied }: Props) {
  const [snapshot, setSnapshot] = useState<Record<string, unknown> | null>(null)
  const [reason, setReason] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const previewRef = useRef(preview)
  previewRef.current = preview
  const keyRef = useRef('')
  useEffect(() => {
    if (!open) return
    let active = true
    setReason('')
    keyRef.current = globalThis.crypto?.randomUUID?.() ?? `admin-${Date.now()}`
    setLoading(true); setError(null); setSnapshot(null)
    previewRef.current().then((value) => { if (active) setSnapshot(value) }).catch((err: Error) => { if (active) setError(err.message) }).finally(() => { if (active) setLoading(false) })
    return () => { active = false }
  }, [open])
  const submit = async () => {
    if (!snapshot || reason.trim().length < 5) return
    setLoading(true)
    try {
      await apply({
        reason: reason.trim(),
        idempotency_key: keyRef.current,
        fingerprint: String(snapshot.fingerprint),
      })
      morphToast.success('Ação aplicada com sucesso.')
      onApplied(); onClose()
    } catch (err) { morphToast.fromError(err); setError(err instanceof Error ? err.message : 'Erro') }
    finally { setLoading(false) }
  }
  return <Dialog open={open} title={title} onClose={onClose} footer={<><Button onClick={onClose}>Cancelar</Button><Button variant="primary" disabled={loading || !snapshot || reason.trim().length < 5} onClick={() => void submit()}>{confirmLabel}</Button></>}>
    {loading && !snapshot ? <p className="text-xs">Preparando resumo…</p> : null}
    {error ? <p role="alert" className="mb-3 text-xs" style={{ color: 'var(--color-danger)' }}>{error}</p> : null}
    {snapshot ? <AdminActionSummary snapshot={snapshot} /> : null}
    <label className="mt-4 block text-xs font-medium">Motivo (obrigatório)<textarea maxLength={1000} placeholder="Descreva o motivo para o histórico administrativo" value={reason} onChange={(event) => setReason(event.target.value)} className="mt-2 min-h-24 w-full rounded-lg border p-3 text-sm" style={{ background: 'var(--bg-input)', borderColor: 'var(--border)', color: 'var(--text)' }} /></label>
    <p className="mt-1 text-xs" style={{ color: 'var(--text-muted)' }}>Use pelo menos 5 caracteres. O motivo ficará registrado no histórico.</p>
  </Dialog>
}
